import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import RevealLines from '@/components/base/RevealLines';
import { services } from '@/mocks/site';

gsap.registerPlugin(ScrollTrigger);

// Continuous blue/violet sequence: pale violet -> mist blue -> muted violet
const panelBg = ['bg-primary-100', 'bg-accent-100', 'bg-primary-300'];

export default function Expertise() {
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = root.current;
    const st = stage.current;
    if (!el || !st) return undefined;
    const mm = gsap.matchMedia();

    // ONE pinned, scrubbed timeline on desktop with motion allowed. Mobile / reduced motion = normal stacked panels.
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      st.classList.add('peel-on');
      const ctx = gsap.context(() => {
        const panels = gsap.utils.toArray<HTMLElement>('.svc-panel');
        const shades = gsap.utils.toArray<HTMLElement>('.svc-shade');
        gsap.set(panels, { zIndex: (i) => panels.length - i, transformOrigin: '50% 0%' });

        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: st,
            start: 'top top',
            end: () => `+=${window.innerHeight * 2.6}`,
            pin: true,
            scrub: 0.6,
            invalidateOnRefresh: true,
            refreshPriority: 1,
          },
        });

        // state 01 hold
        tl.to({}, { duration: 0.4 });
        panels.slice(0, -1).forEach((p, i) => {
          tl.to(p, { yPercent: -112, rotationX: 14, duration: 1, ease: 'power2.in' })
            .fromTo(p.querySelector('.svc-edge'), { opacity: 0 }, { opacity: 1, duration: 0.35 }, '<')
            .to(p.querySelector('.svc-edge'), { opacity: 0, duration: 0.35 }, '<0.65')
            .fromTo(shades[i + 1], { opacity: 0.28 }, { opacity: 0, duration: 1 }, '<-0.65')
            .fromTo(p.nextElementSibling?.querySelector('.svc-inner') ?? {}, { scale: 0.97 }, { scale: 1, duration: 1 }, '<')
            // hold next state so it reads fully
            .to({}, { duration: 0.4 });
        });
      }, el);
      return () => {
        ctx.revert();
        st.classList.remove('peel-on');
        gsap.set(gsap.utils.toArray('.svc-panel, .svc-shade, .svc-edge, .svc-inner', el), { clearProps: 'all' });
      };
    });

    return () => mm.revert();
  }, []);

  return (
    <section id="expertise" ref={root} aria-label="Our expertise" className="relative overflow-x-clip">
      <div className="bg-background-50 px-4 md:px-6 pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <p className="md:col-span-3 font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">Our expertise / 03</p>
          <RevealLines lines={['Different occasions.', 'One extraordinary', 'approach.']} className="md:col-span-9 font-extrabold tracking-[-0.055em] leading-[0.9] text-[clamp(2.4rem,6.6vw,7rem)]" />
        </div>
      </div>

      <div
        ref={stage}
        className="group/peel relative [&.peel-on]:h-[100svh] [&.peel-on]:overflow-hidden [&.peel-on]:[perspective:1600px]"
      >
        {services.map((s, i) => (
          <article
            key={s.no}
            aria-label={`${s.no} ${s.name}`}
            className={`svc-panel relative w-full ${panelBg[i]} lg:h-[100svh] group-[.peel-on]/peel:absolute group-[.peel-on]/peel:inset-0 will-change-transform`}
          >
            <div className="svc-inner relative h-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 px-4 md:px-6 py-14 md:py-16 lg:py-[9svh]">
              <div className="lg:col-span-5 flex flex-col justify-between gap-6">
                <h3 className="font-extrabold tracking-[-0.055em] leading-[0.88] text-[clamp(2.6rem,6vw,6.5rem)]">{s.name}</h3>
                <span className="block font-heading font-extrabold tracking-[-0.07em] leading-[0.8] text-[clamp(5rem,13vw,13rem)]">{s.no}</span>
              </div>
              <div className="lg:col-span-3 flex flex-col justify-end gap-3 lg:pb-4">
                <p className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-700">{s.focus}</p>
                <p className="text-base md:text-lg leading-snug text-foreground-900 max-w-[340px]">{s.copy}</p>
              </div>
              <div className="lg:col-span-4 relative w-full h-[420px] sm:h-[520px] lg:h-full overflow-hidden rounded-[18px]">
                <img src={s.src} alt={s.alt} className="absolute inset-0 h-full w-full object-cover object-top" />
              </div>
            </div>
            {/* darkening of the sheet underneath while the one above lifts */}
            <div aria-hidden className="svc-shade pointer-events-none absolute inset-0 bg-foreground-950 opacity-0" />
            {/* short-lived lower-edge shadow on the outgoing sheet */}
            <div aria-hidden className="svc-edge pointer-events-none absolute inset-x-0 bottom-0 h-24 translate-y-full bg-gradient-to-b from-foreground-950/25 to-transparent opacity-0" />
          </article>
        ))}
      </div>
    </section>
  );
}